import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, CardActions, Button, Tabs, Tab } from '@mui/material';
import { PlayCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getAsana } from 'services/asana/getAsana';
import { getPranayammas } from 'services/pranayamma/getPranayammas';
import { getRelaxations } from "../../services/relaxation/getRelaxation";
import PranayamaList from 'components/PranayamaList';
import relaxationImg from '../../assets/images/relaxation1.jpg';

function PoseCategory() {
  const { courseid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sequenceType = location.state?.sequenceType || 'standalone';
  const studentid = localStorage.getItem('studentid');
  const userid = localStorage.getItem('userid');
  const roleid = localStorage.getItem('roleid');
  const parsedStudentId = (roleid === '5' || !studentid) ? Number(userid) : Number(studentid);

  const [tabValue, setTabValue] = useState(0);

  const { data: asanaData } = useQuery({
    queryKey: ['asanas', courseid, parsedStudentId],
    queryFn: () => getAsana({ courseId: Number(courseid), studentId: parsedStudentId }),
    enabled: !!parsedStudentId && tabValue === 0
  });

  const {
    data: pranayamaData,
    isLoading: pranayamaLoading,
    error: pranayamaError
  } = useQuery({
    queryFn: () => getPranayammas({ courseid: Number(courseid) }),
    queryKey: ['pranayammas', courseid],
    enabled: tabValue === 1
  });

  const {
    data: relaxationData,
    isLoading: relaxationLoading,
    error: relaxationError
  } = useQuery({
    queryFn: () => getRelaxations({ courseid: Number(courseid), studentid: parsedStudentId }),
    queryKey: ['relaxations', courseid],
    enabled: tabValue === 2
  });

  const pranayamas = pranayamaData?.data || [];
  const yogaAsanas = asanaData?.response || [];
  const relaxations = relaxationData?.data || [];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setTabValue(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  useEffect(() => {
    if (location.state?.justCompletedId && tabValue === 1) {
      setTimeout(() => {
        const element = document.getElementById(`pranayama-card-${location.state.justCompletedId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlight-completed');
          setTimeout(() => element.classList.remove('highlight-completed'), 3000);
        }
      }, 600);
    }
  }, [location.state, tabValue]);

  const startContinuousPranayama = () => {
    if (pranayamas.length === 0) return;

    navigate('/pranayama-tracking', {
      state: {
        courseId: courseid,
        selectedId: pranayamas[0].id,
        name: pranayamas[0].name,
        breathing_sequence: pranayamas[0].breathing_sequence,
        duration: pranayamas[0].duration,
        loops: pranayamas[0].loops,
        instructions: pranayamas[0].instructions,
        isContinuousFlow: true,
        currentIndex: 0,
        allPranayamas: pranayamas,
        phaseIndex: -1,
        currentLoop: 1,
        sequenceType: sequenceType
      },
    });
  };

  // ────────────────────────────────────────────────
  //   CHANGED PART ─ only this function is modified
  // ────────────────────────────────────────────────
  const startContinuousRelaxation = () => {
    if (relaxations.length === 0) return;

    navigate('/relaxation-tracking', {
      state: {
        courseId: courseid,
        selectedId: relaxations[0].id,
        name: relaxations[0].track_name,
        duration: relaxations[0].duration,
        instructions: relaxations[0].instructions,
        audio: relaxations[0].audio,           // ← important
        video: relaxations[0].video || null,   // ← important (if exists)
        isContinuousFlow: true,
        currentIndex: 0,
        allRelaxations: relaxations,
        sequenceType: sequenceType
      },
    });
  };

  if (!parsedStudentId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Please log in to continue
        </Typography>
        <Button variant="contained" color="primary" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: 'auto' }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        centered
        sx={{ mb: 4 }}
      >
        <Tab label="Asanas" />
        <Tab label="Pranayama" />
        <Tab label="Relaxation" />
      </Tabs>

      {/* Continuous Asanas */}
      {tabValue === 0 && sequenceType === 'continuous' && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This is a continuous course. Practice all asanas in sequence.
          </Typography>
          {yogaAsanas.some((asana) => asana.completed !== 1) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayCircleOutlined />}
              onClick={() => navigate(`/asanas/${courseid}/continuous`, { state: { sequenceType } })}
            >
              Start Continuous Flow
            </Button>
          )}
        </Box>
      )}

      {/* Continuous Pranayama */}
      {tabValue === 1 && sequenceType === 'continuous' && (
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          {pranayamas.length > 0 ? (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                This is a continuous course. Practice all pranayama practices in sequence.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayCircleOutlined />}
                onClick={startContinuousPranayama}
              >
                Start Continuous Pranayama Flow
              </Button>
            </>
          ) : (
            <Typography variant="h6" color="text.secondary">
              No continuous pranayama available in this course
            </Typography>
          )}
        </Box>
      )}

      {/* Continuous Relaxation */}
      {tabValue === 2 && sequenceType === 'continuous' && (
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          {relaxations.length > 0 ? (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                This is a continuous course. Practice all relaxation practices in sequence.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayCircleOutlined />}
                onClick={startContinuousRelaxation}
              >
                Start Continuous Relaxation Flow
              </Button>
            </>
          ) : (
            <Typography variant="h6" color="text.secondary">
              No continuous relaxation available in this course
            </Typography>
          )}
        </Box>
      )}

      {/* ASANAS CONTENT */}
      {tabValue === 0 ? (
        yogaAsanas.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" color="text.secondary">
              No asanas available
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {yogaAsanas.map((pose) => (
              <Grid item xs={12} sm={6} md={4} key={pose.id}>
                <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ height: 200, overflow: 'hidden' }}>
                    {pose.asanaImage ? (
                      <img src={pose.asanaImage} alt={pose.posturename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ height: '100%', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography>No Image</Typography>
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={600} textAlign="center">
                      {pose.posturename}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Typography variant="body2" color={pose.completed === 1 ? 'success.main' : 'error.main'} fontWeight={500}>
                      {pose.completed === 1 ? 'Completed' : 'Not Started'}
                    </Typography>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      ) : tabValue === 1 ? (
        pranayamas.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" color="text.secondary">
              No pranayama practices available
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <PranayamaList pranayamas={pranayamas} />
          </Grid>
        )
      ) : (
        relaxations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" color="text.secondary">
              No relaxation practices available
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {relaxations.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ height: 200, overflow: 'hidden' }}>
                    <img
                      src={relaxationImg}
                      alt="Relaxation"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="overline" color="primary" fontWeight="bold">
                      {item.category}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {item.track_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {item.short_description}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
                      Duration: {item.duration}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Typography variant="body2" color={item.completed === 1 ? 'success.main' : 'error.main'} fontWeight={500}>
                      {item.completed === 1 ? 'Completed' : 'Not Started'}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayCircleOutlined />}
                      onClick={() =>
                        navigate('/relaxation-tracking', {
                          state: {
                            courseId: courseid,
                            selectedId: item.id,
                            name: item.track_name,
                            duration: item.duration,
                            instructions: item.instructions,
                            audio: item.audio,
                            video: item.video || null,
                            isContinuousFlow: false,
                            currentIndex: 0,
                            allRelaxations: [],
                            sequenceType: sequenceType
                          }
                        })
                      }
                    >
                      Start
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

        )
      )}
    </Box>
  );
}

export default PoseCategory;